import { Modal } from "@JohnLewisPartnership/jl-design-system/dist/components/modal/Modal";
import DeviceSearch from "./DeviceSearch/DeviceSearch";
import { DeviceSearchHeader } from "./DeviceSearch/DeviceSearchHeader/DeviceSearchHeader";
import styles from "./TradeInModal.module.scss";
import classNames from "classnames/bind";
import Grading from "./Grading/Grading";
import PaymentOptions from "./PaymentOptions/PaymentOptions";
import { useTradeInContext } from "../../TradeInContext";
import { sendEvent } from "@JohnLewisPartnership/experiments-utils";
import { TradeInEvents } from "../../TradeIn";
import TradeInUnavailable from "./TradeInUnavailable/TradeInUnavailable";
import { useEffect, useState } from "react";
import { getBasketQuotes } from "./Confirmation/util/getBasketQuotes";
import Confirmation from "./Confirmation/Confirmation";
import { GradingHeader } from "./Grading/GradingHeader/GradingHeader";

const cx = classNames.bind(styles);

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onFetchQuotesComplete: () => void;
  setTradeInModelOpen: (isTradeInModelOpen: boolean) => void;
};

export enum TRADE_IN_STEPS {
  FIND_DEVICE,
  GRADING_QUESTIONS,
  PAYMENT_OPTIONS,
  CONFIRMATION,
  UNAVAILABLE,
}

const TradeInModal = ({
  isOpen,
  onClose,
  onFetchQuotesComplete,
  setTradeInModelOpen,
}: Props) => {
  const {
    currentScreen,
    selectedDevice,
    setCurrentScreen,
    setExistingQuotes,
    shouldCloseModal,
    resetContextData,
    getQuotesFromBasket,
    basketApiBaseUrl,
  } = useTradeInContext();
  const [loading, setLoading] = useState<boolean>(false);

  const modalScreens = [
    DeviceSearch,
    Grading,
    PaymentOptions,
    Confirmation,
    TradeInUnavailable,
  ];
  const getActiveScreen = () =>
    modalScreens[currentScreen] || TradeInUnavailable;

  useEffect(() => {
    const specialOfferListener = () => {
      setTradeInModelOpen(true);
    };

    document.addEventListener(
      TradeInEvents.TRADE_IN_SPECIAL_OFFERS_DRAWER_EVENT,
      specialOfferListener,
    );

    return () => {
      document.removeEventListener(
        TradeInEvents.TRADE_IN_SPECIAL_OFFERS_DRAWER_EVENT,
        specialOfferListener,
      );
    };
  });
  const renderCurrentScreen = () => {
    const ActiveScreen = getActiveScreen();

    return <ActiveScreen />;
  };

  useEffect(() => {
    (async () => {
      const fetchInitialQuotes = async (): Promise<void> => {
        setLoading(true);
        const quotesResponses = await getBasketQuotes(basketApiBaseUrl);
        setExistingQuotes(quotesResponses);
        if (quotesResponses.length > 0) {
          setCurrentScreen(TRADE_IN_STEPS.CONFIRMATION);
        }
        setLoading(false);
        onFetchQuotesComplete();
      };
      if (isOpen) {
        await fetchInitialQuotes();
      }
    })();
  }, [isOpen]);

  useEffect(() => {
    if (shouldCloseModal) {
      resetContextData();
      handleOnClose();
    }
  }, [shouldCloseModal]);

  if (loading) return;

  const modalClassNames = cx(styles.tradeInModal, {
    "tradeInModal--sand": [DeviceSearch, Grading].includes(getActiveScreen()),
  });

  const handleOnClose = () => {
    if (currentScreen === TRADE_IN_STEPS.FIND_DEVICE) {
      sendEvent(TradeInEvents.PW_TRADE_IN_SEARCH_STEP_DROP_OUT);
    }

    resetContextData();
    onClose();
  };

  const displayBackButton = () =>
    [TRADE_IN_STEPS.GRADING_QUESTIONS, TRADE_IN_STEPS.PAYMENT_OPTIONS].includes(
      currentScreen,
    );

  function renderSubHeader() {
    if (currentScreen === TRADE_IN_STEPS.FIND_DEVICE) {
      return <DeviceSearchHeader />;
    }

    if (currentScreen === TRADE_IN_STEPS.GRADING_QUESTIONS) {
      return selectedDevice && <GradingHeader device={selectedDevice} />;
    }
    return null;
  }

  return (
    <Modal
      rootId="page-root"
      isOpen={isOpen}
      title={"Trade In"}
      headerTitle={renderSubHeader()}
      onClose={handleOnClose}
      backButton={displayBackButton()}
      className={modalClassNames}
      onBackButtonClick={() => setCurrentScreen(currentScreen - 1)}
      footer={false}
      height="90%"
      width="768"
    >
      <div className={styles.tradeInModal__content}>
        {renderCurrentScreen()}
      </div>
    </Modal>
  );
};

export default TradeInModal;
